import apiService, { API_URL } from "../apiUtils"
import storeService from "./storeService"
import authService from "../authService"



interface RefundResponse {
    success: boolean
    message: string
}

interface OrderRequest {
    client_id: number
    type: string
    payment: string
    user_id: number
    type_restauration: string
    items: Array<{
        product_id: number
        quantity: number
        deduction: number
        stock_id?: number
        price?: number
        is_restauration?: boolean
        name?: string
        stocks?: Array<{
            stock_id: number
            quantity: number
            price: number
        }>
    }>
    cmiAmount?: number
    cashAmount?: number
    fidelite_points_used?: number
    table_id?: number
}

interface OrderDetailResponse {
    status: string
    message: string
    data: {
        order: {
            id: number
            client_id: number
            client_name: string
            livraison_id: number | null
            table_id: number | null
            table_number: number | null
            type: "POS" | "ONLINE"
            type_restauration: string
            ice: string
            status: "Pending" | "Confirmed" | "Shipped" | "Cancelled" | "Refunded"
            payment: string
            user_id: number
            cmiAmount: string
            cashAmount: string
            card_fee: string
            total_ttc: string
            total_ht: string
            created_at: string
            updated_at: string
            items: Array<{
                order_detail_id: number
                product_id: number
                name: string
                total_quantity: number
                total_price: number
                stocks: Array<{
                    id: number | null
                    quantity: number
                    price: string | number
                    total: string
                    note: string | null
                    is_restaurant: boolean
                }>
            }>
            has_restaurant_products: boolean
        }
        calculation: {
            subtotal: number
            deductions: number
            fidelity_points_used: number
            fidelity_points_value: string
            card_fee: string
            total_after_discounts: number
            final_total: string
            fidelity_points_earned: number
        }
    }
}

// NEW: Interface for restaurant order request
interface RestaurantOrderRequest {
    client_id: number
    table_id: number
    items: Array<{
        product_id: number
        quantity: number
        note?: string
    }>
}

// New interface for payment processing
interface PaymentProcessRequest {
    order_id: number
    payment_method: string
    cmi_amount?: number
    cash_amount?: number
    loyalty_points_used?: number
}

// New interface for adding items to existing order
interface AddItemsRequest {
    order_id: number
    items: Array<{
        product_id: number
        quantity: number
        deduction: number
        stock_id?: number
        price?: number
        is_restauration?: boolean
        name?: string
        stocks?: Array<{
            stock_id: number
            quantity: number
            price: number
        }>
    }>
    user_id: number
}

export interface SaleProduct {
    id: string
    image?: string
    name: string
    category: string
    brand: string
    quantity: number
    unitPrice: number
    buyingPrice: number
    discount: number
    totalPrice: number
    earnings: number
    earnings_net?: number
    status?: "Completed" | "Refunded"
    loyaltyDiscount?: number
}

export interface Sale {
    id: string
    orderNumber: string
    date: string
    time: string
    client: string
    seller: string
    type: "POS" | "ONLINE"
    paymentMethod: "Cash" | "Card" | "Split"
    cardAmount?: number
    cashAmount?: number
    totalAmount: number
    totalDiscount: number
    loyaltyPointsDiscount?: number
    cmiDeduction?: number
    card_fee?: number
    totalEarnings: number
    status: "Completed" | "Pending" | "Cancelled" | "Refunded" | "Confirmed" | "Shipped"
    products: SaleProduct[]
}

export interface ReceiptData {
    order: {
        id: number
        orderNumber?: string
        client: string
        paymentMethod: string
        totalAmount: number
        totalEarnings?: number
        products: {
            name: string
            quantity: number
            price: number
            total: number
            discount?: number
            loyaltyDiscount?: number
        }[]
        seller: string
        date: string
        time: string
        cmiAmount?: number
        cashAmount?: number
        loyaltyPointsUsed?: number
        loyaltyPointsDiscount?: number
        tableId: number | undefined
    }
    data?: {
        calculation?: {
            card_deduction?: number
            subtotal?: number
            deductions?: number
            final_total?: number
            loyalty_points_discount?: number
        }
    }
}

interface FinalizeRestaurantOrderRequest {
  payment: string
  fidelite_points_used?: number
  deductions?: Array<{ order_detail_id: number; amount: number }>
  cmiAmount?: number
  cashAmount?: number
}


interface ApiOrderResponse {
    status: string
    data: ApiOrder[]
}

interface ApiOrder {
    id: number
    client_id: number
    client_name: string
    user_id: number
    user_name: string
    type: "POS" | "ONLINE"
    status: "Pending" | "Confirmed" | "Shipped" | "Cancelled" | "Refunded"
    payment: string
    total_ttc: string
    total_ht: string
    created_at: string
    updated_at: string
    cmiAmount: string
    cashAmount: string
    card_fee: string
    loyalty_point: number
    loyalty_point_value: string
    order_details: OrderDetail[]
    table_id?: number
}

interface OrderDetail {
    id: number
    order_id: number
    quantity: number
    discount: string
    price_after_deduction: string
    refunded: number
    buy_price: string
    earning: string
    earning_net: number
    loyalty_point: number
    loyalty_point_gained: number
    product_id: number
    product_name: string
    product_image: string
}

// ======= CACHING MECHANISM =======
interface SalesCache {
    data: Sale[] | null
    timestamp: number
    expiryMs: number
    dateFilters: {
        [date: string]: Sale[]
    }
}

const salesCache: SalesCache = {
    data: null,
    timestamp: 0,
    expiryMs: 5 * 60 * 1000,
    dateFilters: {},
}

const isCacheValid = (): boolean => {
    return salesCache.data !== null && Date.now() - salesCache.timestamp < salesCache.expiryMs
}

const isDateCached = (date: string): boolean => {
    return salesCache.dateFilters[date] !== undefined && Date.now() - salesCache.timestamp < salesCache.expiryMs
}

const invalidateCache = (): void => {
    salesCache.data = null
    salesCache.timestamp = 0
    salesCache.dateFilters = {}
}

const loyaltyPointValue = 0.5
let cardDeductionRate: number | null = null

const loadLoyaltyPointValue = async (): Promise<number> => {
    try {
        const storeData = await storeService.getStoreData()
        const pointValue = storeData.loyalty_point_value || 0.5

        if (pointValue < 0.01 || pointValue > 5) {
            console.warn(`Loyalty point value (${pointValue}) is outside reasonable range, using default 0.5`)
            return 0.5
        }

        return pointValue
    } catch (error) {
                return 0.5
    }
}

const getCardDeductionRate = async (): Promise<number> => {
    if (cardDeductionRate !== null) {
        return cardDeductionRate
    }

    try {
        const card_deduction = await storeService.getCardDeduction()
        cardDeductionRate = card_deduction || 0
        return cardDeductionRate
    } catch (error) {
                return 0
    }
}

const brandCache = new Map<string, string>()
const categoryCache = new Map<string, string>()

const preloadCommonData = () => {
    const commonBrands = {
        "1": "Royal Canin",
        "2": "Purina",
        "3": "Pedigree",
        "4": "Whiskas",
        "5": "Hill's",
        "6": "Blue Buffalo",
        "7": "IAMS",
        "8": "Fancy Feast",
        "9": "Friskies",
        "10": "Meow Mix",
    }

    const commonCategories = {
        "1": "Dogs",
        "2": "Cats",
        "3": "Birds",
        "4": "Fish",
        "5": "Small Pets",
        "6": "Dog Food",
        "7": "Cat Food",
        "8": "Pet Toys",
        "9": "Pet Accessories",
        "10": "Pet Supplies",
    }

    Object.entries(commonBrands).forEach(([key, value]) => {
        brandCache.set(key, value)
    })

    Object.entries(commonCategories).forEach(([key, value]) => {
        categoryCache.set(key, value)
    })
}

preloadCommonData()

const getBrandName = async (brandId: number): Promise<string> => {
    if (!brandId || brandId <= 0) {
        return "Unknown Brand"
    }

    const cacheKey = brandId.toString()

    if (brandCache.has(cacheKey)) {
        return brandCache.get(cacheKey) || "Unknown Brand"
    }

    try {
        const response = await apiService.get(`/entities/brands/${brandId}`)

        let brandName = `Brand-${brandId}`

        if (response && response.data && response.data.name) {
            brandName = response.data.name
        } else if (response && response.name) {
            brandName = response.name
        }

        brandCache.set(cacheKey, brandName)
        return brandName
    } catch (error: any) {
        if (
            error.message &&
            (error.message.includes("not found") ||
                error.message.includes("404") ||
                (error.response && error.response.status === 404))
        ) {
            const fallbackName = `Unknown Brand (${brandId})`
            brandCache.set(cacheKey, fallbackName)
            return fallbackName
        }

                return `Brand-${brandId}`
    }
}



const convertApiOrderToSale = async (order: ApiOrder): Promise<Sale> => {
    const createdDate = new Date(order.created_at)
    const formattedDate = createdDate.toISOString().split("T")[0]

    const hours = createdDate.getHours().toString().padStart(2, "0")
    const minutes = createdDate.getMinutes().toString().padStart(2, "0")
    const formattedTime = `${hours}:${minutes}`

    const totalDiscount = order.order_details.reduce((sum, detail) => sum + Number.parseFloat(detail.discount || "0"), 0)

    const totalEarnings = order.order_details
        .filter((detail) => detail.refunded === 0)
        .reduce((sum, detail) => sum + Number.parseFloat(detail.earning || "0"), 0)

    const cardAmount = Number.parseFloat(order.cmiAmount || "0")
    const cashAmount = Number.parseFloat(order.cashAmount || "0")

    const cmiDeduction = order.payment === "CMI" ? Number.parseFloat(order.card_fee || "0") : undefined

    const loyaltyPointsDiscount = Number.parseFloat(order.loyalty_point_value || "0")

    const products: SaleProduct[] = order.order_details.map((detail) => {
        const originalTotal = Number.parseFloat(detail.price_after_deduction || "0") + Number.parseFloat(detail.discount || "0")
        const unitPrice = detail.quantity ? originalTotal / detail.quantity : 0

        const buyingPrice = Number.parseFloat(detail.buy_price || "0") / (detail.quantity || 1)
        const discountAmount = Number.parseFloat(detail.discount || "0")
        const totalPrice = Number.parseFloat(detail.price_after_deduction || "0")
        const earnings = Number.parseFloat(detail.earning || "0")
        const earnings_net = detail.earning_net || earnings

        const productStatus = detail.refunded === 1 ? ("Refunded" as const) : ("Completed" as const)

        return {
            id: detail.id.toString(),
            image: detail.product_image ? `${API_URL}/storage/${detail.product_image}` : undefined,
            name: detail.product_name,
            category: "Unknown Category",
            brand: "Unknown Brand",
            quantity: detail.quantity || 0,
            unitPrice: unitPrice,
            buyingPrice: buyingPrice,
            discount: discountAmount,
            loyaltyDiscount: detail.loyalty_point > 0 ? Number((detail.loyalty_point * 0.5).toFixed(2)) : 0,
            totalPrice: totalPrice,
            earnings: earnings,
            earnings_net: earnings_net,
            status: productStatus,
        }
    })

    const allProductsRefunded = products.every((product) => product.status === "Refunded")
    const saleStatus = allProductsRefunded ? "Refunded" : order.status

    return {
        id: order.id.toString(),
        orderNumber: `ORD-${order.id.toString().padStart(4, "0")}`,
        date: formattedDate,
        time: formattedTime,
        client: order.client_name || "Unknown Client",
        seller: order.user_name || "Unknown Seller",
        type: order.type || "POS",
        paymentMethod: order.payment === "CMI" ? "Card" : order.payment === "Split" ? "Split" : "Cash",
        cardAmount: cardAmount > 0 ? cardAmount : undefined,
        cashAmount: cashAmount > 0 ? cashAmount : undefined,
        totalAmount: Number.parseFloat(order.total_ttc || "0"),
        totalDiscount: totalDiscount,
        loyaltyPointsDiscount: loyaltyPointsDiscount > 0 ? Number(loyaltyPointsDiscount.toFixed(2)) : 0,
        cmiDeduction: cmiDeduction,
        card_fee: Number.parseFloat(order.card_fee || "0"),
        totalEarnings: totalEarnings,
        status: saleStatus,
        products: products,
    }
}

const salesService = {
    async getSales(date?: string): Promise<Sale[]> {
        if (date && isDateCached(date)) {
            return salesCache.dateFilters[date]
        }

        if (!date && isCacheValid() && salesCache.data) {
            return salesCache.data
        }

        try {
            const response = await apiService.get<any>(`/orders`)

            if (!response) {
                throw new Error("Unexpected response format from server")
            }

            let ordersData: ApiOrder[] = []

            if (response.status === "success" && Array.isArray(response.data)) {
                ordersData = response.data
            } else {
                                throw new Error("Unable to parse orders data from server response")
            }

            const sales = await Promise.all(ordersData.map((order) => convertApiOrderToSale(order)))

            sales.sort((a, b) => {
                const dateA = new Date(`${a.date}T${a.time}`)
                const dateB = new Date(`${b.date}T${b.time}`)
                return dateB.getTime() - dateA.getTime()
            })

            salesCache.data = sales
            salesCache.timestamp = Date.now()

            if (date) {
                const filteredSales = sales.filter((sale) => sale.date === date)
                salesCache.dateFilters[date] = filteredSales
                return filteredSales
            }

            return sales
        } catch (error) {
                        throw error instanceof Error ? error : new Error("Failed to fetch sales data from server")
        }
    },

    async getSale(id: string): Promise<Sale | undefined> {
        if (isCacheValid() && salesCache.data) {
            const cachedSale = salesCache.data.find((sale) => sale.id === id)
            if (cachedSale) {
                return cachedSale
            }
        }

        try {
            const sales = await this.getSales()
            return sales.find((sale) => sale.id === id)
        } catch (error) {
                        throw error instanceof Error ? error : new Error("Failed to fetch sale")
        }
    },

    async createOrder(
        orderData: {
            client_id: number
            table_id: number | null
            type_restauration: string
            items: {
                product_id: number
                quantity: number
                note: string
            }[]
        }
    ): Promise<any> {
        try {
            if (orderData.type_restauration != "inside") {
                orderData.table_id = null
            }
                        const response = await apiService.post("/orders/restaurant-orders/create", orderData)

            if (!response || !response.data) {
                                throw new Error("Unexpected response format from server")
            }

            invalidateCache()
            return response

        } catch (error: any) {
            
            if (error.data && error.data.errors) {
                const errorMessages = Object.values(error.data.errors).flat().join(", ")
                throw new Error(`Validation failed: ${errorMessages}`)
            }

            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }

            throw error instanceof Error ? error : new Error("Failed to create order. Please try again.")
        }
    }

    ,

    // Add this method to your salesService

    // NEW: Add items to existing order (simplified - no deduction)
    async addItemsToOrder(
        orderId: string | number,
        items: Array<{
            product_id: number
            quantity: number
            note: string
        }>
    ): Promise<any> {
        try {


            const response = await apiService.post(`/orders/${orderId}/add-product`, { items: items })


            return response.data

        } catch (error: any) {
            
            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }

            throw error instanceof Error ? error : new Error(`Failed to add items to order ${orderId}`)
        }
    },

    // NEW: Process payment for existing order
    async processPayment(
        orderId: string | number,
        paymentMethod: string,
        cmiAmount: number | null = null,
        cashAmount: number | null = null,
        loyaltyPointsUsed: number | null = null
    ): Promise<any> {
        try {
            
            const payment = paymentMethod as "CMI" | "Cash" | "Split"

            const paymentData: PaymentProcessRequest = {
                order_id: Number(orderId),
                payment_method: payment
            }

            // Add payment amounts based on method
            if (payment === "Split") {
                paymentData.cmi_amount = cmiAmount || 0
                paymentData.cash_amount = cashAmount || 0
            } else if (payment === "CMI") {
                paymentData.cmi_amount = cmiAmount || 0
            } else if (payment === "Cash") {
                paymentData.cash_amount = cashAmount || 0
            }

            // Add loyalty points if used
            if (loyaltyPointsUsed && loyaltyPointsUsed > 0) {
                paymentData.loyalty_points_used = loyaltyPointsUsed
            }

            const response = await apiService.post(`/orders/${orderId}/process-payment`, paymentData)

            // Invalidate cache since we've updated an order
            invalidateCache()

                        return response
        } catch (error: any) {
            
            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }

            throw error instanceof Error ? error : new Error(`Failed to process payment for order ${orderId}`)
        }
    },

    async processProductRefund(
        saleId: string,
        productId: string,
        passcode: string,
        reason: string,
    ): Promise<RefundResponse> {
        try {
            const response = await apiService.post("/refunds/order-detail", {
                order_detail_id: productId,
                refund_reason: reason,
                admin_password: passcode,
            })

            invalidateCache()

            const isSuccess =
                response?.status === "success" ||
                response?.status === 200 ||
                (response?.data && response?.data?.status === "success")

            if (isSuccess) {
                return {
                    success: true,
                    message: response.message || "Product refunded successfully",
                }
            } else {
                console.warn("Refund API returned unsuccessful response:", response)
                return {
                    success: false,
                    message: response.message || "Failed to process refund",
                }
            }
        } catch (error: any) {
            
            if (error.message && error.message.includes("rate limit")) {
                return {
                    success: false,
                    message: "Server is busy. Please try again in a few moments.",
                }
            }

            return {
                success: false,
                message: error instanceof Error ? error.message : "An unexpected error occurred",
            }
        }
    },

    async processOrderRefund(orderId: string, passcode: string, reason: string): Promise<RefundResponse> {
        try {
            const response = await apiService.post("/refunds/order", {
                order_id: orderId,
                refund_reason: reason,
                admin_password: passcode,
            })

            invalidateCache()

            const isSuccess =
                response?.status === "success" ||
                response?.status === 200 ||
                (response?.data && response?.data?.status === "success")

            if (isSuccess) {
                return {
                    success: true,
                    message: response.message || "Order refunded successfully",
                }
            } else {
                console.warn("Refund API returned unsuccessful response:", response)
                return {
                    success: false,
                    message: response.message || "Failed to process order refund",
                }
            }
        } catch (error: any) {
            
            if (error.message && error.message.includes("rate limit")) {
                return {
                    success: false,
                    message: "Server is busy. Please try again in a few moments.",
                }
            }

            return {
                success: false,
                message: error instanceof Error ? error.message : "An unexpected error occurred",
            }
        }
    },

    async isProductRefunded(saleId: string, productId: string): Promise<boolean> {
        try {
            const sale = await this.getSale(saleId)
            if (sale) {
                const product = sale.products.find((p) => p.id === productId)
                if (product) {
                    return product.status === "Refunded"
                }
            }

            try {
                const response = await apiService.get(`/refunds/check/${productId}`)
                return response && response.refunded === 1
            } catch (error) {
                                return false
            }
        } catch (error) {
                        return false
        }
    },

    async exportSalesReport(
        startDate: string,
        endDate: string,
        format: "csv" | "pdf" = "pdf",
        filters: { status?: string; payment_method?: string; seller?: string } = {},
    ): Promise<Blob> {
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                format: format,
            })

            if (filters.status) {
                params.append("status", filters.status)
            }
            if (filters.payment_method) {
                params.append("payment_method", filters.payment_method)
            }
            if (filters.seller) {
                params.append("seller", filters.seller)
            }

            const token = authService.getToken()
            if (!token) {
                throw new Error("Authentication required")
            }

            const response = await fetch(`${API_URL}/orders/export?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            })

            if (!response.ok) {
                throw new Error(`Failed to export sales report: ${response.statusText}`)
            }

            return await response.blob()
        } catch (error) {
                        throw error instanceof Error ? error : new Error("Failed to export sales report")
        }
    },

    async forceRefresh(): Promise<Sale[]> {
        invalidateCache()
        return this.getSales()
    },

    async downloadOrderPDF(orderId: string): Promise<void> {
        try {
            const response = await apiService.get(`orders/${orderId}/pdf`, {
                responseType: 'blob'
            });

            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `order_${orderId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
                        throw error;
        }
    },

    async updateOrderStatus(orderId: string | number, status: string): Promise<any> {
        try {
            
            const response = await apiService.put(`/orders/${orderId}/status`, {
                status: status
            })

            invalidateCache()

                        return response
        } catch (error: any) {
            
            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }

            throw error instanceof Error ? error : new Error(`Failed to update order ${orderId} status`)
        }
    },

    // NEW: Free table after payment completion
    async freeTable(tableId: string | number): Promise<any> {

    },
    // NEW: Add single product to existing order
    async addProductToOrder(
        orderId: string | number,
        productId: number,
        quantity: number,
        deduction: number
    ): Promise<any> {
        try {
            
            const requestData = {
                product_id: productId,
                quantity: quantity,
                deduction: deduction
            }

            const response = await apiService.post(`/orders/${orderId}/add-product`, requestData)

            // Invalidate cache since we've updated an order
            invalidateCache()

                        return response
        } catch (error: any) {
            
            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }

            throw error instanceof Error ? error : new Error(`Failed to add product to order ${orderId}`)
        }
    },
    // Method to finalize restaurant order
    async finalizeRestaurantOrder(
        orderId: string | number,
        payment: string,
        fidelityPointsUsed?: number,
        deductions?: Array<{ order_detail_id: number; amount: number }>,
        cmiAmount?: number,
        cashAmount?: number
    ): Promise<any> {
        try {

            const requestData: FinalizeRestaurantOrderRequest = {
                payment: payment
            }

            // Send fidelityPointsUsed if it's defined (including 0)
            if (fidelityPointsUsed !== undefined) {
                requestData.fidelite_points_used = fidelityPointsUsed
            }

            // Send deductions if they exist and have items
            if (deductions && deductions.length > 0) {
                requestData.deductions = deductions
            }

            // Add split payment amounts when payment is Split
            if (payment === "Split") {
                if (cmiAmount !== undefined) {
                    requestData.cmiAmount = cmiAmount
                }
                if (cashAmount !== undefined) {
                    requestData.cashAmount = cashAmount
                }
            }

             // Add for debugging

            const response = await apiService.post(`/orders/restaurant-orders/${orderId}/finalize`, requestData)
            invalidateCache()
            return response
        } catch (error: any) {
            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }
            throw error instanceof Error ? error : new Error(`Failed to finalize restaurant order ${orderId}`)
        }
    },
    async getOrderDetail(orderId: string | number): Promise<OrderDetailResponse> {
        try {
            
            const response = await apiService.get<OrderDetailResponse>(`/orders/${orderId}`)

            if (!response) {
                throw new Error("No response received from server")
            }

            if (response.status !== "success") {
                throw new Error(response.message || "Failed to retrieve order details")
            }

                        return response

        } catch (error: any) {
            
            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }

            throw error instanceof Error ? error : new Error(`Failed to fetch order detail ${orderId}`)
        }
    },
    async getPendingOrders(): Promise<OrderDetailResponse[]> {
        try {
            
            const response = await apiService.get(`/orders/getPendingOrders`)

            if (!response) {
                throw new Error("No response received from server")
            }

            if (response.status !== "success") {
                throw new Error(response.message || "Failed to retrieve pending orders")
            }
                                    return response.data

        } catch (error: any) {
            
            if (error.data && error.data.message) {
                throw new Error(error.data.message)
            }

            throw error instanceof Error ? error : new Error(`Failed to fetch pending orders`)
        }
    }
}

export default salesService